<template>
  <button
    v-if="row.kind === 'link'"
    type="button"
    class="w-full cursor-pointer pt-[7px] text-right text-[12px] font-medium leading-[16px] text-green-700"
    @click="row.onClick"
  >
    {{ row.label }}
  </button>

  <div
    v-else
    class="flex justify-between gap-3 border-b border-[#e5e5e5] py-[7px] text-[12px] leading-[16px]"
    :class="row.kind === 'registrations' ? 'items-start' : 'items-center'"
  >
    <span class="shrink-0 text-gray-500">{{ row.label }}</span>

    <Breadcrumb
      v-if="row.kind === 'breadcrumb'"
      :key="row.path.join('/')"
      :items="breadcrumbItems"
      size="sm"
      collapse
      class="flex min-w-0 justify-end"
      @select="handleBreadcrumbSelect"
    />

    <div
      v-else-if="row.kind === 'registrations'"
      class="flex flex-col items-end gap-1"
    >
      <div
        v-for="registration in visibleRegistrations"
        :key="registration.id"
        class="flex items-center gap-1.5"
      >
        <img
          v-if="registration.flagUrl"
          :src="registration.flagUrl"
          alt=""
          class="size-3 shrink-0 rounded-[2px] object-cover"
        />
        <DacrMarkIcon v-else class="size-3 shrink-0 text-gray-800" />
        <span class="text-gray-800">{{ registration.date }}</span>
        <a
          v-if="registration.certificateUrl"
          :href="registration.certificateUrl"
          aria-label="View certificate"
          @click="openCertificateViewer(registration.certificateUrl, $event)"
          target="_blank"
          class="flex items-center gap-0.5 text-green-700"
        >
          <FileTextIcon class="size-3" />
          <ChevronRightIcon class="size-[10px]" />
        </a>
      </div>

      <button
        v-if="hiddenCount"
        type="button"
        class="flex cursor-pointer items-center gap-1 font-medium text-gray-500"
        @click="expanded = !expanded"
      >
        {{ expanded ? 'Show less' : `+${hiddenCount} more` }}
        <ChevronDownIcon
          class="size-[10px]"
          :class="expanded && 'rotate-180'"
        />
      </button>
    </div>

    <span v-else class="text-right text-gray-800">{{ row.value }}</span>
  </div>
</template>

<script setup lang="ts">
import { openCertificateViewer } from '@/features/copyright/certificateViewer'
import { computed, ref } from 'vue'
import {
  DacrMarkIcon,
  FileTextIcon,
  ChevronRightIcon,
  ChevronDownIcon,
} from '@dacr/icons'
import { Breadcrumb, type BreadcrumbItem } from '@dacr/library'
import type { SidebarRow } from './types'

const { row } = defineProps<{ row: SidebarRow }>()

const expanded = ref(false)

const registrations = computed(() =>
  row.kind === 'registrations' ? row.items : []
)

const breadcrumbItems = computed<BreadcrumbItem[]>(() =>
  row.kind === 'breadcrumb' ? row.path.map((label) => ({ label })) : []
)

const visibleRegistrations = computed(() =>
  expanded.value ? registrations.value : registrations.value.slice(0, 3)
)

const hiddenCount = computed(() => Math.max(registrations.value.length - 3, 0))

const handleBreadcrumbSelect = (_item: BreadcrumbItem, index: number) => {
  if (row.kind !== 'breadcrumb') return
  row.onNavigate(row.path.slice(0, index + 1))
}
</script>
